import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { MdClose, MdCancel } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useRooftop, useUser } from '@store';
import { useGetAllLocationsQuery } from '../../locations/queries/getAllLocations.query.generated';
import { useUpdateDeliveryOrderMutation } from '../queries/updateDeliveryOrder.mutation.generated';
import { Locations } from '@gql/base-types';
import { MovePlannerLocationSelect } from '@hopdrive/feature-move-planner';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

interface EditOrderDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** The pending delivery_orders row to edit. */
  order: any | null;
}

const emptyForm = {
  // Order-level
  order_number: '',
  part_number: '',
  quantity: 1 as number,
  billing_term: 'on_account',
  priority: 'normal',
  item_description: '',
  // Destination-level
  recipient_business: '',
  recipient_name: '',
  recipient_phone: '',
  delivery_address: '',
  delivery_lat: null as number | null,
  delivery_lng: null as number | null,
  special_instructions: '',
  has_return_pickup: false,
  return_description: '',
};

type FormState = typeof emptyForm;

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant='overline'
    sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.6, mb: 0.5 }}
  >
    {children}
  </Typography>
);

const EditOrderDialog: React.FC<EditOrderDialogProps> = ({ open, onClose, onSuccess, order }) => {
  const rooftop = useRooftop();
  const user = useUser();
  const customerId = rooftop?.id || 0;
  const userEmail = user?.profile?.email || 'unknown';

  const [updateOrder, { loading }] = useUpdateDeliveryOrderMutation();

  const { data: locationsData, refetch: refetchLocations } = useGetAllLocationsQuery({
    variables: { customerId },
    skip: !customerId,
    fetchPolicy: 'cache-and-network',
  });
  const savedLocations = (locationsData?.locations ?? []) as (Locations | null)[];

  const [form, setForm] = React.useState<FormState>({ ...emptyForm });
  // What the form held when this open seeded it. Null while closed.
  const [seed, setSeed] = React.useState<FormState | null>(null);
  const [location, setLocation] = React.useState<Locations | null>(null);
  const [cancelling, setCancelling] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const busy = loading || cancelling;

  // Seed the form from the order whenever the dialog opens.
  React.useEffect(() => {
    if (!open || !order) {
      setSeed(null);
      return;
    }
    const seeded: FormState = {
      order_number: order.order_number ?? '',
      part_number: order.part_number ?? '',
      quantity: order.quantity ?? 1,
      billing_term: order.billing_term ?? 'on_account',
      priority: order.priority ?? 'normal',
      item_description: order.item_description ?? '',
      recipient_business: order.recipient_business ?? '',
      recipient_name: order.recipient_name ?? '',
      recipient_phone: order.recipient_phone ?? '',
      delivery_address: order.delivery_address ?? '',
      delivery_lat: order.delivery_lat ?? null,
      delivery_lng: order.delivery_lng ?? null,
      special_instructions: order.special_instructions ?? '',
      has_return_pickup: !!order.has_return_pickup,
      return_description: order.return_description ?? '',
    };
    setForm(seeded);
    setSeed(seeded);
    setLocation(
      order.delivery_address
        ? ({
            address: order.delivery_address,
            latitude: order.delivery_lat ?? null,
            longitude: order.delivery_lng ?? null,
          } as any)
        : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.id]);

  // An app update waits while the order has unsaved edits or a save or cancel is running.
  // The address picker writes into the form, so comparing the form covers it.
  const edited =
    seed !== null && (Object.keys(seed) as (keyof FormState)[]).some(k => String(form[k]) !== String(seed[k]));
  useReloadGuard((open && edited) || busy, 'Parts order');

  const update = (field: keyof FormState) => (event: any) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationChange = (loc?: Locations | null) => {
    setLocation(loc || null);
    setForm(prev => ({
      ...prev,
      delivery_address: loc?.address ?? '',
      delivery_lat: loc?.latitude != null ? Number(loc.latitude) : null,
      delivery_lng: loc?.longitude != null ? Number(loc.longitude) : null,
    }));
  };

  // Picking a known location by its business name fills the whole destination.
  const applySavedLocation = (loc: Locations) => {
    setLocation(loc);
    setForm(prev => ({
      ...prev,
      recipient_business: loc.name || loc.nickname || prev.recipient_business,
      recipient_phone: (loc as any).phone || prev.recipient_phone,
      delivery_address: loc.address || '',
      delivery_lat: loc.latitude != null ? Number(loc.latitude) : null,
      delivery_lng: loc.longitude != null ? Number(loc.longitude) : null,
    }));
  };

  const handleClose = () => {
    if (!busy) onClose();
  };

  const confirmCancelOrder = async () => {
    if (!order?.id) return;
    setConfirmOpen(false);
    setCancelling(true);
    try {
      // Cancelled orders drop off the pending queue but the row is retained for
      // audit. Reuses the dealer's existing update permission.
      await updateOrder({
        variables: {
          id: order.id,
          set: { status: 'cancelled', updated_at: new Date().toISOString(), updated_by: userEmail },
        },
      });
      toast.success(`Order ${form.order_number || order.id} cancelled`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to cancel order:', error);
      toast.error(`Failed to cancel order: ${error?.message || 'Unknown error'}`);
    } finally {
      setCancelling(false);
    }
  };

  const handleSubmit = async () => {
    if (!order?.id) return;
    if (!form.recipient_business.trim()) return toast.error('Recipient business is required');
    if (!form.recipient_name.trim()) return toast.error('Contact person is required');
    if (!form.recipient_phone.trim()) return toast.error('Phone number is required');
    if (!form.delivery_address.trim()) return toast.error('Delivery address is required');

    // order_number and dms_order_id are the order's identity — not editable here.
    const set = {
      part_number: form.part_number.trim() || null,
      quantity: Number(form.quantity) || 1,
      billing_term: form.billing_term,
      priority: form.priority,
      item_description: form.item_description.trim() || null,
      recipient_business: form.recipient_business.trim(),
      recipient_name: form.recipient_name.trim(),
      recipient_phone: form.recipient_phone.trim(),
      delivery_address: form.delivery_address.trim(),
      delivery_lat: form.delivery_lat,
      delivery_lng: form.delivery_lng,
      special_instructions: form.special_instructions.trim() || null,
      has_return_pickup: form.has_return_pickup,
      return_description: form.has_return_pickup ? form.return_description.trim() || null : null,
      updated_at: new Date().toISOString(),
      updated_by: userEmail,
    };

    try {
      await updateOrder({ variables: { id: order.id, set } });
      toast.success(`Order ${form.order_number || order.id} updated`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to update order:', error);
      toast.error(`Failed to update order: ${error?.message || 'Unknown error'}`);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              Edit Order
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Update this pending parts delivery order
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size='small' disabled={loading}>
            <MdClose />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* ── Order details ── */}
        <SectionTitle>Order details</SectionTitle>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth size='small'
              label='Order Number'
              value={form.order_number}
              disabled
              helperText='Order number cannot be changed'
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth size='small'
              label='Part Number'
              value={form.part_number}
              onChange={update('part_number')}
              placeholder='51117205996'
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth size='small'
              label='Quantity'
              type='number'
              value={form.quantity}
              onChange={update('quantity')}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size='small'>
              <InputLabel>Billing</InputLabel>
              <Select value={form.billing_term} label='Billing' onChange={update('billing_term')}>
                <MenuItem value='on_account'>On Account</MenuItem>
                <MenuItem value='cod'>COD</MenuItem>
                <MenuItem value='net_30'>Net 30</MenuItem>
                <MenuItem value='urgent'>Urgent</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size='small'>
              <InputLabel>Priority</InputLabel>
              <Select value={form.priority} label='Priority' onChange={update('priority')}>
                <MenuItem value='normal'>Normal</MenuItem>
                <MenuItem value='rush'>Rush</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth size='small'
              label='Item Description'
              value={form.item_description}
              onChange={update('item_description')}
              multiline
              rows={2}
              placeholder='Fender assembly, bumper cover, headlights…'
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        {/* ── Destination ── */}
        <SectionTitle>Destination</SectionTitle>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              freeSolo
              options={(savedLocations.filter(Boolean) as Locations[])}
              getOptionLabel={o => (typeof o === 'string' ? o : o.name || o.nickname || '')}
              inputValue={form.recipient_business}
              onInputChange={(_e, val, reason) => {
                if (reason !== 'reset') setForm(prev => ({ ...prev, recipient_business: val }));
              }}
              onChange={(_e, val) => {
                if (val && typeof val !== 'string') applySavedLocation(val);
              }}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>{o.name || o.nickname}</Typography>
                    {o.address ? <Typography variant='caption' color='text.secondary'>{o.address}</Typography> : null}
                  </Box>
                </li>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth size='small'
                  label='Recipient Business'
                  required
                  placeholder='Search a known shop or type a name'
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <MovePlannerLocationSelect
              rooftopId={customerId}
              required
              label='Delivery Address'
              placeholder='Search saved locations or an address…'
              helperText='Favorites appear first — or search for a new address'
              location={location}
              locations={savedLocations}
              nameOverride={form.recipient_business.trim() || undefined}
              displayValue='address'
              onLocationChange={handleLocationChange}
              onLocationFavorite={() => {
                refetchLocations();
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth size='small'
              label='Contact Person'
              value={form.recipient_name}
              onChange={update('recipient_name')}
              required
              placeholder='John Smith'
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth size='small'
              label='Phone Number'
              value={form.recipient_phone}
              onChange={update('recipient_phone')}
              required
              type='tel'
              placeholder='(555) 123-4567'
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth size='small'
              label='Special Instructions'
              value={form.special_instructions}
              onChange={update('special_instructions')}
              multiline
              rows={2}
              placeholder='Deliver to bay 3, call upon arrival…'
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2.5 }} />

        {/* ── Return pickup ── */}
        <SectionTitle>Return pickup</SectionTitle>
        <FormControlLabel
          control={<Switch checked={form.has_return_pickup} onChange={update('has_return_pickup')} />}
          label='This delivery includes a return pickup'
        />
        {form.has_return_pickup && (
          <Box sx={{ mt: 1.5 }}>
            <TextField
              fullWidth size='small'
              label='Return Description'
              value={form.return_description}
              onChange={update('return_description')}
              multiline
              rows={2}
              placeholder='Old bumper, core return…'
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          color='error'
          onClick={() => setConfirmOpen(true)}
          disabled={busy}
          startIcon={cancelling ? <CircularProgress size={16} color='error' /> : <MdCancel />}
          sx={{ mr: 'auto', textTransform: 'none', fontWeight: 600 }}
        >
          {cancelling ? 'Cancelling…' : 'Cancel order'}
        </Button>
        <Button
          variant='outlined'
          onClick={handleClose}
          disabled={busy}
          sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
        >
          Close
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={busy}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {loading ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth='xs' fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Cancel this order?</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary'>
          Order{' '}
          <Box component='span' sx={{ fontFamily: '"JetBrains Mono", monospace', color: 'text.primary' }}>
            {form.order_number || order?.id}
          </Box>{' '}
          will be removed from the queue. This can't be undone from here.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant='outlined'
          onClick={() => setConfirmOpen(false)}
          sx={{ textTransform: 'none', color: 'text.secondary', borderColor: 'divider' }}
        >
          Keep order
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={confirmCancelOrder}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Cancel order
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default EditOrderDialog;
